import { NavClientService, IPageQueryConstraint, IPageObj, PagePropertyType, IPageProperty, kendoColumn, NavGridVisibility, IObservableArray, IPageMetaData, NavPageMetaData, NavDtFormat, IEnumMapping, IEnumMappings } from '@common/navdata';
import { CompatibilityService } from '@common/services/compatibilityservice';
import { IFDRW_Workflow_Running_Instances, InvoiceDataItem } from '@common/rwf';
import { AsObservableArray } from '@common/navkendo';
import { Type_10, Status, IWorkflow_FWF_Type_Descriptions, IFWFData, IWorkflow_FWF_Instance_Completed, IWorkflow_FWF_Types } from './rwfpages.generated';
import { fn } from '@common/utils';

/** Access to FWF Related Data */
export class FwfDataService {

    static $inject = ["CompatibilityService"];

    constructor(readonly $c: CompatibilityService) {
        this.NavClient = $c.resolve("NavClient");

        this.IsFWFActive = true;
        $c.resolve("Rwf")("RwfBootstrap").OnResolved(r => {
            if (typeof r.RwfSetup.IsFWFActive === "boolean") {
                this.IsFWFActive = r.RwfSetup.IsFWFActive;
            }
        }, true);
    }

    readonly NavClient: NavClientService;

    IsFWFActive: boolean;

    private get NavUrl() { return this.$c.resolve("Rwf")("RwfBootstrap").EnsureResolved().NavUrlInfo; }

    public QueryFWFInstancesCompleted(fwfType: string, PageCount: number, constraints?: IPageQueryConstraint[]) {
        constraints = constraints || [];
        constraints.push(<IPageQueryConstraint<IWorkflow_FWF_Instance_Completed>>{ FieldName: "TypeID", Value: fwfType });
        return this.$c.all({
            Props: this.GetFWFAdditionalProperties(fwfType),
            Data: this.NavClient.ReadMultiple<IWorkflow_FWF_Instance_Completed>({
                Constraints: constraints,
                PageCount: PageCount,
                PageName: "Workflow_FWF_Instance_Completed"
            }, this.NavUrl)
        }).then(info => {
            // we must ensure "early" to be types in kendo way ...
            info.Data.Data = fn.ToArray(AsObservableArray(info.Data.Data));
            this.NormalizeInvoicedata(info.Data.Data, info.Props.map(item => item.Name));
            return info.Data;
        });
    }

    public GetFWFAdditionalTypes() {
        return this._fwfTypes || (this._fwfTypes =
            this.NavClient.ReadMultiple<IWorkflow_FWF_Types>({
                PageCount: 0,
                PageName: "Workflow_FWF_Types",
                Constraints: []
            }, this.NavUrl).then(d => d.Data));
    }

    private _fwfTypes: PromiseLike<IWorkflow_FWF_Types[]>

    /** Determine Additional Columns for FWF Items by FWF Type Code */
    public GetFWFAdditionalColumns(fwftype: string) {
        return this.GetFWFAdditionalProperties(fwftype).then(data => {
            return data.map(item => {
                var result = kendoColumn(item as IPageProperty, "FWFData.");
                const hat = result.headerAttributes || (result.headerAttributes = {});
                hat["title"] = item.Title + "(" + fwftype + ")";
                return result;
            });
        });
    }

    public GetFWFAdditionalProperties(fwftype: string) {
        const GetFWFAdditionalProperties = '__GetFWFAdditionalProperties';
        var colInfo: Record<string, PromiseLike<IPageProperty[]>> = this[GetFWFAdditionalProperties] || (this[GetFWFAdditionalProperties] = {});
        return colInfo[fwftype] || (colInfo[fwftype] = this.NavClient.ReadMultiple<IWorkflow_FWF_Type_Descriptions>({
            PageCount: 0,
            PageName: "Workflow_FWF_Type_Descriptions",
            Constraints: [{ FieldName: "TypeID", Value: fwftype }]
        }).then(data => {
            var props = this.fwfColMaps[fwftype] = <Record<string, IFWFType>>{};
            // Boolean Code Date Decimal GUID Integer Option Text Time
            const fwfTypeMap: Partial<Record<TFWFFieldType, PagePropertyType>> = {
                "Date": "DateTime",
                "Boolean": "Boolean",
                "Decimal": "Decimal",
                "Integer": "Int32",
            };
            return data.Data.map(item => {
                const fieldName = NormalizeFWFFieldName(item.MaindataTableID, item.FieldID, fwftype);
                const curr = props[fieldName] = item as IFWFType;
                let type = fwfTypeMap[item.FieldType] || "String";
                const result = <IPageProperty>{
                    Title: item.FieldCaption || item.FieldName,
                    Name: fieldName,
                    Type: curr.Type = type,
                    GridVisibility: item.InitialVisible === false ? NavGridVisibility.InitialHidden : NavGridVisibility.Default
                };

                if (curr.FieldOptionCaption) {
                    result.Type = curr.Type = <any>"Option";
                    result.EnumMappings = <IEnumMappings>curr.FieldOptionCaption.split(",").map((item, idx) => <IEnumMapping>{
                        Title: item.trim() || "<leer>",
                        Value: idx,
                        Enum: item
                    });
                    result.EnumMappings._TemplateIgnore = true;
                }

                return result;
            });
        }));

    }

    public NormalizeInvoicedata(data: InvoiceDataItem[] | IObservableArray<InvoiceDataItem>, mandatoryProps?: string[]) {
        var types = Object.getOwnPropertyNames(this.fwfColMaps);
        if (types.length > 0) {
            data.filter((item: InvoiceDataItem) => item.FWFData && item.FWFData.length > 0 && item.FWFData.__Handled !== true)
                .forEach((item: InvoiceDataItem & IFDRW_Workflow_Running_Instances) => {
                    const fwfData = item.FWFData || [];
                    const globType = item["TypeID"]
                    const fwfType = this.fwfColMaps[item.WorkflowType || globType]; // Assumption: Each Item has only one type!
                    if (fwfType) {
                        fwfData.forEach(item => {
                            const fieldName = NormalizeFWFFieldName(item.TableID, item.FieldID, item.TypeID || globType);
                            fwfData[fieldName] = NormalizeFWFFieldValue(item.Value, fwfType[fieldName]?.FieldType);
                        });
                        fwfData.__Handled = true;
                        if (mandatoryProps?.length > 0 && fn.FindFirst(mandatoryProps, p => !(p in fwfData))) {
                            item["_MissingData"] = true;
                        }

                    } else {
                        item["_MissingData"] = true;
                    }
                });
        }
    }

    /** Stored add. FWF Col Data by FWFType Code, to allow applying values ... */
    private readonly fwfColMaps: Record<string, Record<string, IFWFType>> = {};
}

type TFWFFieldType = "Boolean" | "Code" | "Date" | "Decimal" | "GUID" | "Integer" | "Option" | "Text" | "Time";
interface IFWFType extends IWorkflow_FWF_Type_Descriptions {
    Type?: PagePropertyType;
    FieldType: TFWFFieldType;
}

export interface IWFDataItem extends IFWFData {
    TypeID?: string;
}

// TODO Dependency to Workflow_FWF_FinishedDataPageDefinition.ReadMultiple FieldName Resolution!
function NormalizeFWFFieldName(tableId: number, fieldId: number, fwfType: string) {
    return ("_" + tableId + "_" + fieldId + "_" + fwfType).replace(/[^a-zA-Z0-9_]/g, "_");
}

function NormalizeFWFFieldValue(value: string, fwfType: TFWFFieldType) {
    switch (fwfType) {
        case "Boolean":
            return value === "Ja" ? true : (value === "Nein" ? false : (!value ? false : value));
        case "Decimal":
            value = value.replace(/\./g, "").replace(",", ".");
            var result = parseFloat(value);
            return isNaN(result) ? value : result;
        case "Integer":
            var result = parseInt(value);
            return isNaN(result) ? value : result;
        case "Date":
            var dateMatch = value?.match(/^([\d]+)\.([\d+]+)\.([\d]+)/);
            if (dateMatch?.length > 2) {
                const year = parseInt(dateMatch[3]);
                value = <any>new Date(year < 70 ? year + 2000 : year, parseInt(dateMatch[2]) - 1, parseInt(dateMatch[1]));
            }
            return value;
        default:
            return value;
    }
}


